import { useMemo } from 'react';

import { Environment } from '@playq/octopus-common';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';
import {
  AppLadderId,
  LadderInstanceDimensions,
  TeamLadderInstanceDimensions,
  TeamLadderScores,
} from '@playq/services-clerk';

import { services2 } from '/api/services2';
import { useEitherQuery, IUseQueryOptionsExtended, useQueryPrefetch } from '/api/service-hooks';

import { laddersQueryTags } from './useQueryLadders';

export const queryTeamsLadderScoresKeys = laddersQueryTags.concat('queryTeamsLadderScores');

export const useQueryTeamsLadderScores = (
  env: Environment,
  id: AppLadderId | undefined,
  aggregatedLadderDimensions: LadderInstanceDimensions | TeamLadderInstanceDimensions | undefined,
  iterator: OffsetLimit,
  maybeBucketId: number | undefined,
  options?: IUseQueryOptionsExtended<TeamLadderScores | undefined, GenericFailure | Error>
) => {
  const keys = useMemo(
    () => queryTeamsLadderScoresKeys.concat(env, id, aggregatedLadderDimensions, iterator, maybeBucketId),
    [aggregatedLadderDimensions, env, id, iterator, maybeBucketId]
  );

  const isValid = useMemo(
    () => id !== undefined && aggregatedLadderDimensions instanceof LadderInstanceDimensions,
    [aggregatedLadderDimensions, id]
  );

  const enabled = useMemo(() => (options?.enabled ?? true) && isValid, [options?.enabled, isValid]);
  const enablePrefetch = useMemo(
    () => (options?.enablePrefetch ?? true) && isValid,
    [options?.enablePrefetch, isValid]
  );

  const res = useEitherQuery(
    keys,
    () =>
      services2.teamScoreSupportService.queryTeamLadderInstanceScores(
        env,
        id as AppLadderId,
        aggregatedLadderDimensions as LadderInstanceDimensions,
        iterator,
        maybeBucketId
      ),
    {
      ...options,
      enabled,
    }
  );

  const scores = useMemo(() => res.data?.scores, [res.data?.scores]);

  useQueryPrefetch({
    keys,
    enabled: enablePrefetch,
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    hasMore: scores !== undefined && scores.length === iterator.limit,
    args: [env, id as AppLadderId, aggregatedLadderDimensions as LadderInstanceDimensions, iterator, maybeBucketId],

    serviceMethod: services2.teamScoreSupportService.queryTeamLadderInstanceScores.bind(
      services2.teamScoreSupportService
    ),
  });

  // default value is not provided for checks if request was made
  return { ...res, scores: res.data?.scores, maybeBucket: res.data?.maybeBucket, queryKeys: keys };
};
